// src/components/ClaimDetail.js - NEW COMPONENT
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Calendar, DollarSign, User, FileText, Download,
  Check, Clock, X, AlertCircle, Eye
} from 'lucide-react';

const ClaimDetail = ({ user }) => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClaimDetail();
  }, [claimId]);

  const fetchClaimDetail = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`/api/claims/${claimId}/`);
      setClaim(response.data);
      
    } catch (error) {
      console.error('Error fetching claim detail:', error);
      if (error.response?.status === 404) {
        setError('Claim not found.');
      } else {
        setError('Failed to load claim details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const iconProps = { size: 20 };
    switch (status) {
      case 'pending': return <Clock {...iconProps} className="text-yellow-600" />;
      case 'approved': return <Check {...iconProps} className="text-green-600" />;
      case 'denied': return <X {...iconProps} className="text-red-600" />;
      case 'paid': return <Check {...iconProps} className="text-blue-600" />;
      default: return <AlertCircle {...iconProps} className="text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'denied': return 'bg-red-100 text-red-800';
      case 'paid': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const downloadDocument = (doc) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="claim-detail-container">
        <div className="loading-detail">
          <div className="loading-spinner"></div>
          <p>Loading claim details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="claim-detail-container">
        <div className="error-detail">
          <AlertCircle size={48} className="error-icon" />
          <h2>Error Loading Claim</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')} className="back-to-dashboard-btn">
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-detail-container">
      {/* Header */}
      <header className="claim-detail-header">
        <div className="header-content">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="back-button"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
          
          <div className="claim-title">
            <h1>Claim #{claim.id}</h1>
            <div className={`claim-status-badge ${getStatusColor(claim.status)}`}>
              {getStatusIcon(claim.status)}
              <span>{claim.status_display}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="claim-detail-content">
        {/* Attention Required Banner */}
        {claim.status === 'attention_required' && (
          <div className="attention-banner">
            <AlertCircle size={22} />
            <div>
              <strong>Action Required</strong>
              <p>Your claim needs attention. Please review the response from CoRe Group Benefits below and take the necessary action.</p>
            </div>
          </div>
        )}

        {/* Claim Overview */}
        <div className="detail-section">
          <div className="section-header">
            <FileText className="section-icon" />
            <h2>Claim Overview</h2>
          </div>
          
          <div className="overview-grid">
            <div className="overview-item">
              <label>Claim Type</label>
              <span className="value">{claim.claim_type_display}</span>
            </div>
            <div className="overview-item">
              <label>Patient</label>
              <span className="value">{claim.patient_name}</span>
            </div>
            <div className="overview-item">
              <label>Total Amount</label>
              <span className="value amount">{formatCurrency(claim.total_amount)}</span>
            </div>
            <div className="overview-item">
              <label>Submitted</label>
              <span className="value">{formatDate(claim.submitted_date)}</span>
            </div>
            {claim.approved_amount && (
              <div className="overview-item">
                <label>Approved Amount</label>
                <span className="value amount">{formatCurrency(claim.approved_amount)}</span>
              </div>
            )}
            {claim.reviewed_date && (
              <div className="overview-item">
                <label>Reviewed</label>
                <span className="value">{formatDate(claim.reviewed_date)}</span>
              </div>
            )}
          </div>

          {claim.admin_response && (
            <div className="admin-response-section">
              <div className="admin-response-header">
                <FileText size={18} />
                <h3>Response from CoRe Group Benefits</h3>
              </div>
              <p className="admin-response-text">{claim.admin_response}</p>
            </div>
          )}

          {claim.member_notes && (
            <div className="notes-section">
              <h3>Your Notes:</h3>
              <p className="notes-text">{claim.member_notes}</p>
            </div>
          )}
        </div>

        {/* Services */}
        <div className="detail-section">
          <div className="section-header">
            <DollarSign className="section-icon" />
            <h2>Services ({claim.line_items.length})</h2>
          </div>
          
          <div className="services-list">
            {claim.line_items.map((item, index) => (
              <div key={item.id} className="service-item">
                <div className="service-header">
                  <span className="service-number">Service {index + 1}</span>
                  <span className="service-cost">{formatCurrency(item.cost)}</span>
                </div>
                <div className="service-details">
                  <div className="service-info">
                    <User size={16} />
                    <span>Patient: {item.beneficiary_display_name}</span>
                  </div>
                  <div className="service-info">
                    <Calendar size={16} />
                    <span>{formatDate(item.service_date)}</span>
                  </div>
                  <div className="service-description">
                    <strong>{item.description}</strong>
                  </div>
                  {item.tooth_number && (
                    <div className="service-extra">
                      <span>Tooth: {item.tooth_number}</span>
                    </div>
                  )}
                  {item.provider_name && (
                    <div className="service-extra">
                      <span>Provider: {item.provider_name}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Documents */}
        {claim.documents.length > 0 && (
          <div className="detail-section">
            <div className="section-header">
              <FileText className="section-icon" />
              <h2>Documents ({claim.documents.length})</h2>
            </div>
            
            <div className="documents-list">
              {claim.documents.map((doc) => (
                <div key={doc.id} className="document-item">
                  <div className="document-info">
                    <FileText size={20} className="document-icon" />
                    <div className="document-details">
                      <span className="document-name">{doc.original_filename}</span>
                      <span className="document-meta">
                        {doc.file_size_human} • {formatDate(doc.uploaded_at)}
                      </span>
                    </div>
                  </div>
                  <button 
                    onClick={() => downloadDocument(doc)}
                    className="download-button"
                  >
                    <Download size={16} />
                    <span>View</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimDetail;